import {
  Badge,
  Button,
  createColumnHelper,
  DEFAULT_TABLE_HEADER_ROW_HEIGHT,
  DEFAULT_TABLE_ROW_HEIGHT,
  EmptyState,
  Icon,
  Input,
  MessagePanel,
  Table,
  TableColumnDef,
} from '@dittolive/anvil'
import { InfoIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import React, { useMemo, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import type { AuthenticationProvider, WebhookOperations } from '../../../types'
import CreateProviderModal from './CreateProviderModal'
import DeleteProviderModal from './DeleteProviderModal'
import EditProviderModal from './EditProviderModal'
import WebhookActionMenu from './WebhookActionMenu'

// The max height of the table is 5 rows + the header row. Everything beyond that
// will be scrollable. This is mostly sylistic, so it doesn't grow too large.
const MAX_HEIGHT =
  DEFAULT_TABLE_HEADER_ROW_HEIGHT + DEFAULT_TABLE_ROW_HEIGHT * 5

type Webhook = AuthenticationProvider & {
  enableDqlAuthWebhook?: boolean
}

const columnHelper = createColumnHelper<Webhook>()
const baseColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ getValue, row }) => {
      const isLegacy = row.original.legacy

      return (
        <div className="flex items-center gap-2">
          <span data-testid="webhookName" className="break-all">
            {getValue()}
          </span>
          {isLegacy && row.original.enableDqlAuthWebhook && (
            <Badge colorScheme="sunset" size="xs">
              Legacy
            </Badge>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor('url', {
    header: 'URL',
    cell: ({ getValue }) => (
      <span data-testid="webhookUrl" className="break-all">
        {getValue()}
      </span>
    ),
  }),
]

type Props = {
  /**
   * List of configured providers
   */
  providers: AuthenticationProvider[]
  /**
   * Whether authentication is enabled
   */
  isAuthEnabled: boolean
  /**
   * Operations for managing webhooks
   */
  operations: WebhookOperations
  /**
   * Whether DQL auth webhook feature flag is enabled
   */
  enableDqlAuthWebhook?: boolean
  /**
   * Existing webhook names for validation (usually derived from providers)
   */
  existingWebhookNames: string[]
  /**
   * Optional terminology overrides
   */
  terminology?: {
    databaseText?: string
    authModeText?: string
  }
  /**
   * Optional render function for additional content in the edit modal
   * Receives the webhook provider name
   */
  renderEditModalChildren?: (providerName: string) => React.ReactNode
}

/**
 * Component used for managing the list of auth webhooks that are configured for an app.
 */
export default function AuthenticationProvidersList({
  providers,
  isAuthEnabled,
  operations,
  enableDqlAuthWebhook = false,
  existingWebhookNames,
  terminology,
  renderEditModalChildren,
}: Props) {
  const databaseText = terminology?.databaseText ?? 'app'
  const authModeText = terminology?.authModeText ?? 'playground'

  const webhooks = useMemo<Webhook[]>(
    () =>
      providers.map((provider) => ({
        ...provider,
        enableDqlAuthWebhook,
      })),
    [providers, enableDqlAuthWebhook],
  )

  const [isShowingError, setIsShowingError] = useState(false)
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false)
  const [editingProvider, setEditingProvider] = useState<{
    name: string
    url: string
    legacy?: boolean
  }>()
  const [deletingProvider, setDeletingProvider] = useState<string>()
  const [filter, setFilter] = useState('')

  const handleUpdateWebhook = async (
    forProvider: string,
    name: string,
    url: string,
    legacy: boolean,
  ) => {
    try {
      if (isShowingError) {
        setIsShowingError(false)
      }
      await operations.onUpdate(forProvider, name, url, legacy)
      setIsShowingError(false)
    } catch {
      setIsShowingError(true)
    } finally {
      setEditingProvider(undefined)
    }
  }

  const handleCreateWebhook = async (
    name: string,
    url: string,
    legacy: boolean,
  ) => {
    try {
      if (isShowingError) {
        setIsShowingError(false)
      }
      await operations.onCreate(name, url, legacy)
      setIsShowingError(false)
    } catch {
      setIsShowingError(true)
    } finally {
      setIsCreatingWebhook(false)
    }
  }

  const handleDeleteWebhook = async (providerName: string) => {
    try {
      if (isShowingError) {
        setIsShowingError(false)
      }
      await operations.onDelete(providerName)
      setIsShowingError(false)
    } catch {
      setIsShowingError(true)
    } finally {
      setDeletingProvider(undefined)
    }
  }

  const columns = useMemo<TableColumnDef<Webhook>[]>(
    () => [
      ...baseColumns,
      columnHelper.display({
        id: 'actions',
        cell: ({ row: { original } }) => (
          <WebhookActionMenu
            onSelectForEdit={() =>
              setEditingProvider({
                name: original.name,
                url: original.url,
                legacy: original.legacy,
              })
            }
            onSelectForDeletion={() => setDeletingProvider(original.name)}
          />
        ),
        size: 30,
        meta: {
          isActionColumn: true,
        },
        enableSorting: false,
      }),
    ],
    [],
  )

  const renderContent = () => {
    return (
      <AutoSizer disableHeight>
        {({ width }: { width: number }) => (
          <Table
            columns={columns}
            data={webhooks}
            width={width}
            height={(rowCount) =>
              rowCount > 0
                ? Math.min(
                    MAX_HEIGHT,
                    DEFAULT_TABLE_HEADER_ROW_HEIGHT +
                      DEFAULT_TABLE_ROW_HEIGHT * rowCount,
                  )
                : 156
            }
            activeOnHover={false}
            tableCellClassName="first:pl-6"
            tableHeaderCellClassName="first:pl-6"
            emptyStateRenderer={() => (
              <EmptyState
                className="py-4"
                message={
                  !!filter.length
                    ? 'No authentication provider webhooks match your search'
                    : 'No authentication provider webhooks configured'
                }
              />
            )}
            options={{
              state: {
                globalFilter: filter,
              },
            }}
          />
        )}
      </AutoSizer>
    )
  }

  const withAuthText =
    terminology?.authModeText === 'Development'
      ? 'Authenticated'
      : 'with authentication'

  return (
    <>
      <CreateProviderModal
        show={isCreatingWebhook}
        onCancel={() => setIsCreatingWebhook(false)}
        onConfirm={handleCreateWebhook}
        existingWebhookNames={existingWebhookNames}
        enableDqlAuthWebhook={enableDqlAuthWebhook}
      />

      <EditProviderModal
        webhook={editingProvider}
        onCancel={() => setEditingProvider(undefined)}
        onConfirm={handleUpdateWebhook}
        existingWebhookNames={existingWebhookNames.filter(
          (provider) => provider !== editingProvider?.name,
        )}
        enableDqlAuthWebhook={enableDqlAuthWebhook}
      >
        {editingProvider?.name &&
          renderEditModalChildren?.(editingProvider.name)}
      </EditProviderModal>

      <DeleteProviderModal
        show={!!deletingProvider}
        webhook={deletingProvider || ''}
        onClose={() => setDeletingProvider(undefined)}
        onConfirm={() => handleDeleteWebhook(deletingProvider || '')}
      />

      {!isAuthEnabled && (
        <div className="bg-background-surface-secondary p-4">
          <div className="flex">
            <Icon
              className="text-foreground-subtle"
              svg={<InfoIcon aria-hidden="true" />}
            />
            <div className="flex-1 md:flex md:justify-between">
              <p
                className="text-foreground-subtle"
                data-testid="disabledWebhooksMessage"
              >
                Editing the authentication provider webhooks is disabled because
                this {databaseText} is currently in {authModeText.toLowerCase()}{' '}
                mode. Select the &quot;{withAuthText}&quot; setting above to
                enable authentication provider webhook customization.
              </p>
            </div>
          </div>
        </div>
      )}
      {isShowingError && (
        <MessagePanel
          variant="danger"
          message="There was an error updating the webhooks configuration. Please contact support if the error persists."
        />
      )}

      <div className="flex h-12 items-center gap-x-2 px-4 sm:px-6">
        <Input
          className="relative h-8 w-full"
          leadingIcon={
            <Icon
              className="text-foreground-subtle size-4"
              svg={<MagnifyingGlassIcon />}
            />
          }
          value={filter}
          onChange={(e) => setFilter(e.target.value?.toLowerCase() || '')}
          placeholder="Search webhooks"
          disabled={providers.length === 0}
        />

        <Button variant="primary" onClick={() => setIsCreatingWebhook(true)}>
          New webhook
        </Button>
      </div>

      {renderContent()}
    </>
  )
}
